import { times } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import { isAtMostMobile, withBreakpointRange } from '@optimus-web/breakpoints';
import { BreakpointRangeWithFallback } from '../types';
import { getBreakpointRangeValueFallback } from '../utils/breakpointsFallback';
import { CalendarService } from '../utils/services';
import CalendarMonthSelector from './CalendarMonthSelector';
import { updateContainerWidth } from './MonthlyCalendarUtils/LifeCycleManagement';
import { generateMonths, getMonthSelectedPosition, getMonthUniquePosition } from './MonthlyCalendarUtils/utils';
import {
    CalendarMonthGridContainerElement,
    CalendarMonthSelectorWrapperElement,
    CalendarViewHolderElement,
} from './style';

export interface CalendarOriginalProps<D, M, Y> {
    getCalendarService: () => CalendarService<D, M, Y>;
    numberOfMonths?: number;
    month?: M;
    onMonthClick: Function;
    minDate?: D;
    maxDate?: D;
}

type CalendarProps<D, M, Y> = CalendarOriginalProps<D, M, Y> & {
    breakpointRange: BreakpointRangeWithFallback;
};

type CalendarType = <D, M, Y>(
    props: CalendarOriginalProps<D, M, Y>
) => React.ReactElement<CalendarOriginalProps<D, M, Y>>;

export const MonthCalendar = <D, M, Y>({
    numberOfMonths = 1,
    getCalendarService,
    onMonthClick,
    month,
    minDate,
    maxDate,
    breakpointRange,
    ...other
}: CalendarProps<D, M, Y>) => {
    const allowedRange = { minDate, maxDate };
    const monthGridContainerElement = useRef<HTMLDivElement>(null);

    const currentDate: D = getCalendarService().getCurrentDate();
    const initialMonth = month ?? getCalendarService().getMonthOfDate(getCalendarService().getCurrentDate());

    const [getMonth, setMonth] = useState(initialMonth);
    const [getMonthSelected, setMonthSelected] = useState(initialMonth);

    const theme = useTheme();

    useEffect(() => {
        updateContainerWidth(monthGridContainerElement.current, numberOfMonths, breakpointRange, theme);
    }, [numberOfMonths, monthGridContainerElement, breakpointRange, theme]);

    useEffect(() => {
        if (month && !getCalendarService().isMonthEqual(month, getMonth)) {
            setMonth(month);
            // set also the month selected so the calendar reacts to input changes
            setMonthSelected(month);
        }
    }, [month, getMonth, getCalendarService]);

    const handleMonthSelect = (event: React.MouseEvent | React.KeyboardEvent<HTMLButtonElement>, selectedMonth: M) => {
        const months = generateMonths({
            month: getMonth,
            numberOfMonths,
            getCalendarService,
        });
        const monthSelectedPosition = getMonthUniquePosition(getMonthSelected, months, getCalendarService);
        const newMonth = getCalendarService().subtractMonths(selectedMonth, monthSelectedPosition);

        if (!getCalendarService().isMonthEqual(selectedMonth, getMonthSelected)) {
            setMonth(newMonth);
            setMonthSelected(newMonth);
        }
        onMonthClick?.(event, newMonth);
    };

    const months = generateMonths({ month: getMonthSelected, numberOfMonths, getCalendarService });
    const monthSelectedIndex = getCalendarService().getMonthIndex(getMonthSelected);
    const monthSelectedPosition = getMonthSelectedPosition(monthSelectedIndex, months, getCalendarService);
    const weeksOfMonth = (index: number) =>
        getCalendarService().getWeeksOfMonth(getCalendarService().addMonths(getMonth, index));
    const numberOfWeeks = Math.max(...times(numberOfMonths, (i) => weeksOfMonth(i).length));

    const isMobile = isAtMostMobile(getBreakpointRangeValueFallback(breakpointRange));

    const monthGridContainerProps = {
        ref: monthGridContainerElement,
    };

    const viewHolderElementProps = {
        numberOfWeeks,
        numberOfMonths,
        standalone: true,
        isMobile,
        ...other,
    };

    const monthSelectorWrapperProps = {
        numberOfWeeks,
        monthSelectorVisible: true,
        monthSelectedPosition,
        standalone: true,
        skipAnimateOut: true,
        isMobile,
    };

    return (
        <CalendarViewHolderElement {...viewHolderElementProps}>
            <CalendarMonthSelectorWrapperElement {...monthSelectorWrapperProps}>
                <CalendarMonthSelector
                    numberOfWeeks={numberOfWeeks}
                    selectedMonth={getMonthSelected}
                    monthSelectedPosition={monthSelectedPosition}
                    onChange={handleMonthSelect}
                    getCalendarService={getCalendarService}
                    monthSelectorVisible
                    currentDate={currentDate}
                    allowedRange={allowedRange}
                    isMonthlyPicker
                />
            </CalendarMonthSelectorWrapperElement>
            <CalendarMonthGridContainerElement {...monthGridContainerProps} />
        </CalendarViewHolderElement>
    );
};

export default withBreakpointRange(MonthCalendar) as unknown as CalendarType;
